import { Button, Icon, Modal, Typography } from '@globalmed/atlas-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import styles from './NotificationOnMeeting.module.css';
import { useWindowBlankAction } from '@/hooks/useWindowBlankAction';
import { useChatsStore } from '@/stores/chats/chats.store';
import { useMeetingNotificationStore } from '@/stores/meetingNotification';
import { useUsersStore } from '@/stores/users';
import { ERouterNames } from '@/types';
import { EChatEvent } from '@/types/chats';
import { nameFormat } from '@/utils/valuesFormat';

export const NotificationOnMeeting = () => {
  const { meeting, removeMeeting } = useMeetingNotificationStore((state) => ({
    meeting: state.meeting,
    removeMeeting: state.removeMeeting,
  }));

  const { getChat, postChatEvent } = useChatsStore((state) => ({
    getChat: state.getChat,
    postChatEvent: state.postChatEvent,
  }));

  const checkRoleIds = useUsersStore((state) => state.checkRoleIds);

  const roleId = useUsersStore((state) => state.roleId);
  const { t } = useTranslation();

  const { runWithWindow, loading } = useWindowBlankAction();

  const onDecline = async () => {
    if (!meeting?.chatId || !roleId) {
      removeMeeting(null);
      return;
    }
    const response = await getChat(meeting?.chatId);
    if (
      response?.events.some(
        ({ userInstitutionRoleId, type }) =>
          checkRoleIds([userInstitutionRoleId]) &&
          [
            EChatEvent.JOINED,
            EChatEvent.CHAT_ACCEPTED,
            EChatEvent.CHAT_DECLINED,
          ].includes(type),
      )
    ) {
      removeMeeting(null);
      return;
    }
    await postChatEvent(meeting?.chatId, {
      userInstitutionRoleId: roleId,
      type: EChatEvent.CHAT_DECLINED,
    });
    removeMeeting(null);
  };

  const onAccept = async () => {
    if (!meeting?.chatId || !roleId) {
      removeMeeting(null);
      return;
    }
    const response = await getChat(meeting?.chatId);
    if (
      response?.events.some(
        ({ userInstitutionRoleId, type }) =>
          checkRoleIds([userInstitutionRoleId]) &&
          [
            EChatEvent.JOINED,
            EChatEvent.CHAT_ACCEPTED,
            EChatEvent.CHAT_DECLINED,
          ].includes(type),
      )
    ) {
      removeMeeting(null);
      return;
    }
    await runWithWindow(async (window) => {
      const encounterUrl = generatePath(
        `/${ERouterNames.MEDICAL_STAFF_ENCOUNTER}`,
        {
          encounterId: meeting?.encounterId ?? '',
          chatId: meeting.chatId,
        },
      );
      const chatUrl = generatePath(`/${ERouterNames.MEDICAL_STAFF_CHAT}`, {
        chatId: meeting.chatId,
      });
      await postChatEvent(meeting?.chatId, {
        userInstitutionRoleId: roleId,
        type: EChatEvent.CHAT_ACCEPTED,
      });
      removeMeeting(null);
      window.location.href = meeting?.encounterId ? encounterUrl : chatUrl;
    });
  };

  const providerName = useMemo(() => {
    if (!meeting) return null;
    const user = meeting.participants[0].user;
    return nameFormat(user?.firstName, user?.lastName);
  }, [meeting]);
  const modalHeaderText = meeting?.encounterId
    ? t('notifications.video-call-request-header')
    : t('notifications.chat-request-header');
  const providerNameWrapper = {
    providerName,
  };
  const modalBodyText = meeting?.encounterId
    ? t('notifications.video-call-request-body', providerNameWrapper)
    : t('notifications.chat-request-body', providerNameWrapper);

  return (
    <Modal
      open={!!meeting}
      title={
        <>
          <Typography
            as="span"
            fontSize="20px"
            fontWeight={600}
            sx={{ lineHeight: 1.5 }}
          >
            {modalHeaderText}
          </Typography>
          <button
            type="button"
            onClick={onDecline}
            disabled={loading}
            aria-label={t('close')}
            className={styles.closeButton}
          >
            <Icon
              name="XClose"
              size="24px"
              color="var(--colors-text-default)"
              decorative
              data-testid="close-icon"
            />
          </button>
        </>
      }
      body={
        <div className={styles.container}>
          <Typography>{modalBodyText}</Typography>
          <div className={styles.btns}>
            <Button
              onClick={onDecline}
              wide
              disabled={loading}
              color="destructive"
              text={t('decline')}
            />
            <Button
              onClick={onAccept}
              wide
              loading={loading}
              color="success"
              text={t('accept')}
            />
          </div>
        </div>
      }
    />
  );
};
